import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { TextField } from '@material-ui/core'
import SydButton from '../../../../../../commonDesign/Button'
import SydLabel from '../../../../../../commonDesign/SydLabel'
import SydModalActions from '../../../../../../commonDesign/SydModal/SydModalActions'
import Switch from '../../../../../../molecules/Switch'

/**
 * Single subordinate predicate editor form, used inside a modal.
 * Can operate in "add" mode (blank initial state) or "edit" mode (pre-filled).
 */
function PredicatesEditor ({ predicate, onSave, onCancel }) {
  const [code, setCode] = useState(predicate?.code || '')
  const [description, setDescription] = useState(predicate?.description || '')
  const [expression, setExpression] = useState(predicate?.expression || '')
  const [parameterized, setParameterized] = useState(predicate?.parameterized || false)

  const isValid = code.trim() && expression.trim() && /^[a-z0-9-]+$/.test(code.trim())
  const isNew = !predicate?.code

  const handleSave = useCallback(() => {
    if (!isValid) return
    onSave({
      code: code.trim(),
      description: description.trim() || null,
      expression: expression.trim(),
      parameterized,
      requiredParams: predicate?.requiredParams || []
    })
  }, [code, description, expression, parameterized, predicate, isValid, onSave])

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <SydLabel required label='Predicate Code' helperText='Lowercase letters, numbers, and hyphens (e.g., managed-only)'>
          <TextField
            value={code}
            onChange={(e) => setCode(e.target.value)}
            size='small'
            fullWidth
            placeholder='managed-only'
            disabled={!isNew}
          />
        </SydLabel>

        <SydLabel label='Description' helperText='Human-readable description shown in the dropdown'>
          <TextField
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            size='small'
            fullWidth
            placeholder='Managed accounts only'
          />
        </SydLabel>

        <SydLabel required label='Expression' helperText='SQL WHERE fragment applied when this predicate is selected'>
          <TextField
            value={expression}
            onChange={(e) => setExpression(e.target.value)}
            size='small'
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            placeholder="e.g., accountType = 'managed'"
            InputProps={{
              style: { fontFamily: 'var(--font-mono, monospace)', fontSize: '0.875rem' }
            }}
          />
        </SydLabel>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Switch
            checked={parameterized}
            onChange={(e) => setParameterized(e.target.checked)}
          />
          <span style={{ fontSize: '0.875rem' }}>Parameterized</span>
        </div>
      </div>

      <SydModalActions>
        <SydButton variant='ghost' size='md' onClick={onCancel}>
          Cancel
        </SydButton>
        <SydButton
          disabled={!isValid}
          variant='primary'
          size='md'
          onClick={handleSave}
        >
          {isNew ? 'Add Filter' : 'Save Changes'}
        </SydButton>
      </SydModalActions>
    </div>
  )
}

PredicatesEditor.propTypes = {
  predicate: PropTypes.shape({
    code: PropTypes.string,
    description: PropTypes.string,
    expression: PropTypes.string,
    parameterized: PropTypes.bool,
    requiredParams: PropTypes.arrayOf(PropTypes.string)
  }),
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
}

PredicatesEditor.defaultProps = {
  predicate: null
}

export default PredicatesEditor
